import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { getMessage } from 'util/locale';
import { useSelector } from 'react-redux';
import { Link } from '@tcc/crate';
import { Tag } from '@tcc/fds';

import { getMessageKeyByEligibility } from 'util/productEligibility';
import ScriptingCard from './ScriptingCard';
import ScriptingCardTrigger from './ScriptingCardTrigger';
import { getUpsellProductForFeature } from 'product-features/selectors';
import { FEATURE_NAMES, KEYS_BY_PRODUCT } from 'constants/features';
import { SCRIPT_CREATOR_URL } from '../constants/urls';
import BetaTag from '../common/BetaTag';

const baseString = 'vra-scripting-card';

const AdvancedScriptingCard = ({
  flow = 'single',
  isBulkEnabled,
  canCreateAdvancedScript,
  cardTitle = getMessage('title-text__advanced-script'),
  currentCropCanAdvancedScript,
  onClickAdvancedScriptTrigger
}) => {

  const eligibility = useSelector((state) => getUpsellProductForFeature(state, FEATURE_NAMES.ADVANCED_SCRIPTING));

  const productKey = useMemo(() => KEYS_BY_PRODUCT[eligibility.eligibleProduct], [eligibility.eligibleProduct]);

  const { isDisabled, isPrimary, testId, buttonTextKey } = useMemo(() => {
    let isDisabled = false;
    let isPrimary = false;
    let testId = `upgrade-${productKey}`;
    let buttonTextKey = `button-text__upgrade--${productKey}`;

    if (!currentCropCanAdvancedScript) {
      buttonTextKey = 'button-text__disabled--corn-only';
      testId = 'corn-only';
      isDisabled = true;
    } else if (canCreateAdvancedScript) {
      buttonTextKey = 'button-text__start';
      testId = 'create-advanced-prescription';
      isPrimary = true;
    }

    return { isDisabled, isPrimary, testId, buttonTextKey };
  }, [currentCropCanAdvancedScript, canCreateAdvancedScript, productKey]);

  const triggerClassString = useMemo(() => classNames({
    [`${baseString}__trigger`]: true,
    [`${baseString}__trigger__upgrade`]: !canCreateAdvancedScript
  }), [canCreateAdvancedScript]);

  const upsaleEligibility = useMemo(() => ({
    eligibility,
    canCreateScript: canCreateAdvancedScript
  }), [eligibility, canCreateAdvancedScript]);

  const disabledMessageKey = useMemo(() => canCreateAdvancedScript
    ? null
    : getMessageKeyByEligibility(upsaleEligibility), [canCreateAdvancedScript, upsaleEligibility]);

  const baseButton = useMemo(() => (
    <ScriptingCardTrigger
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      onClick={onClickAdvancedScriptTrigger}
      testId={`${flow === 'bulk' ? 'bulk-' : ''}${testId}`}
      text={getMessage(buttonTextKey)}
      triggerClassString={triggerClassString}
    />
  ), [isDisabled, isPrimary, onClickAdvancedScriptTrigger, flow, testId, buttonTextKey, triggerClassString]);

  const { description, label, button } = useMemo(() => ({
    bulk: {
      description: 'text__advanced-script__bulk-prescriptions-intro',
      label: (
        <>
          <Tag
            fill="soft"
            sides="roundedRect"
            density="medium"
            testId="bulk-prescriptions-badge"
            color="primary"
          >
            {getMessage('text__advanced-script__bulk-prescriptions-badge')}
          </Tag>
          <BetaTag />
        </>
      ),
      button: isDisabled ? baseButton : (
        <Link
          href={SCRIPT_CREATOR_URL.BULK_PRESCRIPTIONS}
          data-test-id="bulk-prescriptions-link"
        >
          {baseButton}
        </Link>
      )
    },
    single: {
      description: 'text__advanced-script__intro',
      label: (
        <Tag
          fill="outline"
          sides="roundedRect"
          density="medium"
          testId="single-prescription-badge"
        >
          {getMessage('text__advanced-script__single-prescription-badge')}
        </Tag>
      ),
      button: baseButton
    }
  }[flow]), [flow, isDisabled, baseButton]);

  if (!eligibility.eligibleProduct && !canCreateAdvancedScript) {
    return null;
  }

  return (
    <ScriptingCard
      body={getMessage(description)}
      optionType='advanced'
      title={cardTitle}
      cardTitle={cardTitle}
      disabledMessageKey={disabledMessageKey}
      label={isBulkEnabled && label}
      button={button}
    />
  );
};
AdvancedScriptingCard.propTypes = {
  flow: propTypes.string,
  isBulkEnabled: propTypes.bool,
  canCreateAdvancedScript: propTypes.bool.isRequired,
  cardTitle: propTypes.string,
  currentCropCanAdvancedScript: propTypes.bool.isRequired,
  onClickAdvancedScriptTrigger: propTypes.func,
};
export default AdvancedScriptingCard;
